import { useState } from "react";
import { Link, Navigate } from "@tanstack/react-router";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsWorkingMode } from "@/contexts/AppModeContext";
import { useActiveLocation } from "@/contexts/ActiveLocationContext";
import { useAuth } from "@/hooks/useAuth";
import { useCookingSessions } from "@/hooks/cooking-sessions/queries";
import { countView, isSessionLate, multiViewSessionsParams, resolveView } from "@/lib/multi-view";
import type { BatchScale } from "@/lib/recipes/staged-method";
import { AddRecipesDialog } from "./AddRecipesDialog";
import { GridView } from "./GridView";
import { MultiViewHeader, type MultiViewLayout, type MultiViewMode } from "./MultiViewHeader";
import { SessionChip } from "./SessionChip";
import { SpotlightView } from "./SpotlightView";
import { TabsView } from "./TabsView";
import { deselectSession, selectSession, useSelectedSessionIds } from "./selection-store";
import { useMultiViewSelectionLifecycle } from "./useMultiViewSelectionLifecycle";
import { useSessionClock } from "./useSessionClock";
import { useSpotlight } from "./useSpotlight";
import { sessionTone } from "@/lib/multi-view";

/** Whether a recipe that goes late moves into the spotlight, until the cook says otherwise. */
export const AUTO_FOCUS_LATE_DEFAULT = true;

interface MultiViewPageProps {
  view: MultiViewMode;
  layout: MultiViewLayout;
  onViewChange: (view: MultiViewMode) => void;
  onLayoutChange: (layout: MultiViewLayout) => void;
}

/**
 * Multi View (YC-12): the recipes ticked on Prep, run together on one screen,
 * one at a time in tabs or all at once in the grid and the spotlight.
 *
 * The recipes are whatever is ticked on Prep (kept while the user moves between
 * the two pages, not beyond). Their status comes from the same list the board
 * reads and polls, and one clock ticks every timer on the screen.
 */
export function MultiViewPage({ view, layout, onViewChange, onLayoutChange }: MultiViewPageProps) {
  const { t } = useLingui();
  useMultiViewSelectionLifecycle();
  const working = useIsWorkingMode();
  const { hasPermission, isLoading: authLoading } = useAuth();
  const { activeLocation } = useActiveLocation();
  const canRead = hasPermission("cooking-session:read");
  const ids = useSelectedSessionIds();

  const query = useCookingSessions(multiViewSessionsParams(activeLocation?.id), { enabled: canRead && !authLoading });
  const allSessions = query.data?.data ?? [];
  const sessions = resolveView(allSessions, ids);
  const viewIds = sessions.map((s) => s.id);
  const secondsOf = useSessionClock(query.dataUpdatedAt);
  const lateIds = sessions.filter((s) => isSessionLate(s, secondsOf(s))).map((s) => s.id);

  const [tabId, setTabId] = useState<number | null>(null);
  const [autoFocusLate, setAutoFocusLate] = useState(AUTO_FOCUS_LATE_DEFAULT);
  const [spotlightId, pickSpotlight] = useSpotlight(viewIds, lateIds, autoFocusLate);
  const [scales, setScales] = useState<Record<number, BatchScale>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!authLoading && !canRead) {
    return (
      <Navigate
        to="/unauthorized"
        search={{ reason: "permission_denied", from: "/multi-view", requiredPermission: "cooking-session:read" }}
        replace
      />
    );
  }
  // Multi View is a Working-mode screen; Build mode has no way in and no business here.
  if (!working) return <Navigate to="/dashboard" replace />;

  const scaleOf = (id: number): BatchScale => scales[id] ?? 1;
  const setScaleOf = (id: number, scale: BatchScale) => setScales((s) => ({ ...s, [id]: scale }));
  const candidates = allSessions.filter((s) => !viewIds.includes(s.id));
  const counts = countView(sessions, secondsOf);
  const isSpotlight = view === "multi" && layout === "spotlight";

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-3" data-testid="multi-view-page">
      <MultiViewHeader
        locationName={activeLocation?.name}
        counts={counts}
        view={view}
        onViewChange={onViewChange}
        layout={layout}
        onLayoutChange={onLayoutChange}
        autoFocusLate={autoFocusLate}
        onAutoFocusLateChange={setAutoFocusLate}
        onAddRecipes={() => setAddOpen(true)}
      />

      {error && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-xl bg-(--color-danger-bg) px-4 py-3 text-sm text-(--color-danger-text)"
        >
          <span>{error}</span>
          <Button type="button" size="sm" variant="outline" onClick={() => setError(null)}>
            {t`Dismiss`}
          </Button>
        </div>
      )}

      {query.isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-xl bg-(--color-danger-bg) px-4 py-3 text-sm text-(--color-danger-text)"
        >
          <span>{t`The recipes couldn't be loaded.`}</span>
          <Button type="button" size="sm" variant="outline" onClick={() => void query.refetch()}>
            {t`Try again`}
          </Button>
        </div>
      ) : query.isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : sessions.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-20 text-center" data-testid="multi-view-empty">
          <p className="text-lg font-semibold text-(--color-text-primary)">{t`No recipes in this view`}</p>
          <p className="text-sm text-(--color-text-secondary)">{t`Tick recipes on Prep to watch them together.`}</p>
          <div className="flex items-center gap-2">
            <Button asChild variant="brand-outline">
              <Link to="/prep">{t`Back to Prep`}</Link>
            </Button>
            <Button type="button" onClick={() => setAddOpen(true)}>
              {t`Add recipes`}
            </Button>
          </div>
        </div>
      ) : view === "tabs" ? (
        <TabsView
          sessions={sessions}
          activeId={tabId}
          onActiveChange={setTabId}
          secondsOf={secondsOf}
          scaleOf={scaleOf}
          onScaleChange={setScaleOf}
          onError={setError}
        />
      ) : (
        <>
          <div
            role={isSpotlight ? "tablist" : "list"}
            aria-label={t`Recipes in the view`}
            className="flex flex-wrap items-center gap-2"
          >
            {sessions.map((session) => (
              <SessionChip
                key={session.id}
                name={session.recipeName}
                tone={sessionTone(session, secondsOf(session))}
                seconds={secondsOf(session)}
                active={isSpotlight ? session.id === spotlightId : undefined}
                onSelect={isSpotlight ? () => pickSpotlight(session.id) : undefined}
                onRemove={() => deselectSession(session.id)}
              />
            ))}
          </div>
          {isSpotlight ? (
            <SpotlightView
              sessions={sessions}
              spotlightId={spotlightId}
              onSpotlightChange={pickSpotlight}
              secondsOf={secondsOf}
              scaleOf={scaleOf}
              onScaleChange={setScaleOf}
              onError={setError}
            />
          ) : (
            <GridView
              sessions={sessions}
              secondsOf={secondsOf}
              scaleOf={scaleOf}
              onExpand={(id) => {
                pickSpotlight(id);
                onLayoutChange("spotlight");
              }}
              onError={setError}
            />
          )}
        </>
      )}

      <AddRecipesDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        candidates={candidates}
        onAdd={(session) => selectSession(session.id, session.status === "ON_DECK" ? "onDeck" : "cookingNow")}
      />
    </div>
  );
}
